import { useCallback, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton, InputAdornment, TextField } from "@mui/material";

import { OpenAccountUser } from "@/types/openAccount";
import { EntityProps } from "@/types/util";
import { ValidationParams } from "@/types/validation";

import { handleChange } from "@/lib/validation";
import { securitySettingsPinValidation } from "@/lib/validation/securitySettings";

import { useOpenAccount } from "@/context/openAccount";

export default function PinForm<
  T extends Pick<OpenAccountUser, "pin" | "pinConfirmation">
>(props: EntityProps<T>) {
  const { errors, openAccountUser, setErrors, setOpenAccountUser } =
    useOpenAccount();

  const [showPinValue, setShowPinValue] = useState(false);
  const [showPinConfirmationValue, setShowPinConfirmationValue] =
    useState(false);

  const onChangeParams = useCallback<
    () => Pick<
      ValidationParams<T, Pick<OpenAccountUser, "pin" | "pinConfirmation">>,
      "validation" | "setEntity" | "setErrors"
    >
  >(() => {
    return {
      validation: securitySettingsPinValidation,
      setEntity: props.set,
      setErrors: props.setErrors,
    };
  }, [props.set, props.setErrors, setOpenAccountUser, setErrors]);

  function onChange(
    value: string,
    field: keyof Pick<OpenAccountUser, "pin" | "pinConfirmation">,
    confirmationValue?: string
  ) {
    const { validation, setEntity, setErrors } = onChangeParams();

    handleChange<T, Pick<OpenAccountUser, "pin" | "pinConfirmation">>({
      confirmationValue,
      field,
      setEntity,
      setErrors,
      validation,
      value,
    });
  }

  return (
    <>
      <TextField
        key="pin-value"
        label="Set Pin"
        fullWidth
        variant="standard"
        value={openAccountUser?.pin ?? ""}
        type={showPinValue ? "text" : "password"}
        error={errors.pin !== undefined}
        helperText={errors.pin}
        slotProps={{
          input: {
            inputMode: "numeric",
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPinValue((prev) => !prev)}
                  edge="end"
                >
                  {showPinValue ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        onChange={(event) => {
          onChange(event.target.value, "pin");
          onChange(
            openAccountUser?.pinConfirmation?.toString() ?? "",
            "pinConfirmation",
            event.target.value
          );
        }}
        sx={{ mr: 2 }}
      />
      <TextField
        key="confirm-pin-value"
        label="Confirm Pin"
        fullWidth
        variant="standard"
        value={openAccountUser?.pinConfirmation ?? ""}
        type={showPinConfirmationValue ? "text" : "password"}
        error={errors.pinConfirmation !== undefined}
        helperText={errors.pinConfirmation}
        slotProps={{
          input: {
            inputMode: "numeric",
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPinConfirmationValue((prev) => !prev)}
                  edge="end"
                >
                  {showPinConfirmationValue ? (
                    <VisibilityOff />
                  ) : (
                    <Visibility />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        onChange={(event) =>
          onChange(
            event.target.value,
            "pinConfirmation",
            (openAccountUser?.pin ?? "").toString()
          )
        }
        sx={{ mr: 2 }}
      />
    </>
  );
}
